import React, {useState, useRef, useEffect, useMemo} from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./index.module.scss"
import SecondaryButton from "../../../../../components/ui/secondary-button"
import { ImportFileIcon } from "../../../../../components/icons/import-file"
import Address from "../../../../../components/ui/address-input/input"
import NormalInputLog from "../../../../../components/ui/normal-input/input"
import {usePost, useGet} from "../../../../../utils/functions"
import CSRFToken from "../../../../../utils/csrftoken"
import { addBins} from "../../../../../redux/authSlice"
import * as XLSX from 'xlsx';

const ImportBinsTab = () => {
  const [form, setForm] = useState([]);
  const { data, loading, postMultipleBins } = usePost({ url: "add-bin/", form })
  const { fetch: fetchZones, fetchedData: zones, isLoading: zonesLoading } = useGet("userzones/");
  const bins = useSelector(state => state.user.binList) || [];
  const [selectedFile, setSelectedFile] = useState();
  const [importedBins, setImportedBins] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef();
  const dispatch = useDispatch()

  useEffect(() => {
    fileInputRef.current.addEventListener('change', handleFileUpload);
    fetchZones();
  }, []);

  const zoneMapping = useMemo(() => {
    const mapping = {};
    zones.forEach(zone => {
      mapping[zone.name] = zone.id;
    });
    return mapping;
  }, [zones]);

  const handleFileUpload = event => {
    const file = event.target.files[0];
    setSelectedFile(file);

    // Check the file size
    const fileSizeInMB = file.size / (1024*1024);
    const maxSizeInMB = 5;
    if (fileSizeInMB > maxSizeInMB) {
      alert(`File is too large (${fileSizeInMB.toFixed(2)} MB). Maximum allowed size is ${maxSizeInMB} MB.`);
      return;
    }
  
    // Read the file as binary string
    const reader = new FileReader();
    reader.onload = function(e) {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });

      // Get the first worksheet
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];

      // Convert the worksheet to JSON
      const json = XLSX.utils.sheet_to_json(worksheet);

      // Validate the data before setting it to state
      const validatedJson = json.map(bin => {
        if (!bin.Name || !bin.Address || typeof bin['Fill Level'] !== 'number' || !bin.Zone) {
          // Invalid data, return null
          return null;
        }
        return bin;
      }).filter(Boolean); // Remove null values

      // Set the imported bins to your state
      setImportedBins(validatedJson);
    };
    reader.readAsBinaryString(file);
  };

  const handleInputChange = (event, index, field) => {
    const newBins = [...importedBins];
    if (field === 'selected') {
      newBins[index][field] = event.target.checked;
    } else {
      newBins[index][field] = event.target.value;
    }
    setImportedBins(newBins);
  };

  const handleBinUpdate = (updatedBin, index) => {
    const newBins = [...importedBins];
    newBins[index] = { ...newBins[index], ...updatedBin };
    setImportedBins(newBins);
  };

  useEffect(() => {
    if (isSubmitted) {
      Promise.all(form.map(bin => postBin(bin)));
    }
  }, [form, isSubmitted]);

  const handleSubmit = async(e) => {
    e.preventDefault();
    const selectedBins = importedBins.filter(bin => bin.selected);
    
    const promises = selectedBins.map(bin => postBin(bin));
    Promise.all(promises)
      .then((responses) => {
        // If all posts were successful, add the new bins to the Redux store
        dispatch(addBins([...bins, ...responses]));
      })
      .catch((error) => {
        console.error("An error occurred while adding bins.");
      });
  };

  const postBin = (bin) => {
    // Remove the selected field from bin
    const { selected, ...binWithoutSelected } = bin;

    // Map the new headers back to the old ones
    const binWithOldHeaders = {
      name: binWithoutSelected.Name,
      address: binWithoutSelected.Address,
      fill_level: binWithoutSelected['Fill Level'],
      zone: binWithoutSelected.zone,
    };
    
    // Create a synthetic event object
    const syntheticEvent = {
      preventDefault: () => {},
    };

    return postMultipleBins(null, binWithOldHeaders).catch(error => {
      console.error('An error occurred while adding a bin:', error);
      return Promise.reject(error);
    });
  };
  

  return (
    
    <article>
      <main className={styles.main}>
        <ImportFileIcon className={styles.icon} />
        <label htmlFor="fileUpload" className={styles.customFileUpload}>Choose file</label>
        <div className={styles.text}>
          <p className={styles.textp}>Supported format Excel(.xlsx)</p>
          <p>Headers: Name, Address, Fill Level, Zone</p>
        </div>
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} id="fileUpload" accept=".xlsx, .xls, .csv" />
      </main>
      <form className={styles.form} onSubmit={handleSubmit}>
        <CSRFToken />
        {importedBins.length > 0 && (
          <>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Fill Level</th>
                  <th>Zone</th>
                  <th>Select</th>
                </tr>
              </thead>
              <tbody>
                {importedBins.map((bin, index) => (
                  <tr key={index}>
                    <td><NormalInputLog type="text" showIcon={false} value={bin.Name} onChange={e => handleInputChange(e, index, 'Name')} /></td>
                    <td><Address key={bin.Address} form={{...bin, Address: bin.Address}} label="Address" setForm={(updatedBin) => handleBinUpdate(updatedBin, index)} required={true} /></td>
                    <td><NormalInputLog type="text" showIcon={false} value={bin["Fill Level"]} onChange={e => handleInputChange(e, index, 'Fill Level')} /></td>
                    <td><NormalInputLog type="text" value={bin.Zone} onChange={e => handleInputChange(e, index, 'Zone')} placeholder="Enter zone" setForm={(updatedBin) => handleBinUpdate(updatedBin, index)} form={bin} /></td>
                    <td>
                      <input type="checkbox" checked={bin.selected} onChange={e => handleInputChange(e, index, 'selected')}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.btns}>
              <SecondaryButton type="submit" title="Add Selected Bins" />
            </div>
          </>
        )}
      </form>
    </article>
  )
}
export default ImportBinsTab;